import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from '../utils/toast';
import { Check, X, Lock } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Plan tier hierarchy for upgrade-only logic
const PLAN_TIER_ORDER = { core: 0, advanced: 1, enterprise: 2 };

const PlansPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [pricingPlans, setPricingPlans] = useState({});
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [user, setUser] = useState(null);
  const [currentPlanTier, setCurrentPlanTier] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (!token) {
      navigate('/signin');
      return;
    }
    
    if (userData) {
      setUser(JSON.parse(userData));
    }
    
    fetchPlans();
    fetchPricingPlans();
    fetchCurrentSubscription();
  }, [navigate]);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/plans`);
      setPlans(response.data.plans);
    } catch (error) {
      toast.error('Failed to fetch plans');
    } finally {
      setLoading(false);
    }
  };

  const fetchPricingPlans = async () => {
    try {
      const response = await axios.get(`${API}/cms/pricing-plans`);
      setPricingPlans(response.data.plans || {});
    } catch (error) {
      console.error('Failed to fetch CMS pricing plans:', error);
    }
  };

  const fetchCurrentSubscription = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/subscriptions/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Check if subscription exists and has plan data
      const subscription = response.data.subscription;
      if (!subscription || !response.data.has_subscription) {
        console.warn('No active subscription found');
        setCurrentPlanTier(null);
        return;
      }
      
      // Extract plan tier from the subscription object (use plan_tier if available, otherwise parse from plan_name)
      const planTier = subscription.plan_tier || subscription.plan_name?.toLowerCase().replace(/\s+plan$/i, '').trim();
      console.log('Current subscription plan tier:', planTier, 'from plan_name:', subscription.plan_name);
      setCurrentPlanTier(planTier);
    } catch (error) {
      console.error('Failed to fetch current subscription:', error);
      toast.error('Unable to fetch current plan. Please refresh the page.');
      setCurrentPlanTier(null);
    }
  };

  // Get CMS features for a plan type
  const getPlanFeatures = (planTier) => {
    const tierKey = planTier?.toLowerCase() || 'core';
    if (pricingPlans[tierKey]) {
      return pricingPlans[tierKey].features || [];
    }
    return [];
  };

  // Check if a plan can be selected (only upgrades allowed)
  const canSelectPlan = (planTier) => {
    if (!currentPlanTier) return true; // No current subscription, can select any
    const currentTierValue = PLAN_TIER_ORDER[currentPlanTier.toLowerCase()] ?? -1;
    const targetTierValue = PLAN_TIER_ORDER[planTier?.toLowerCase()] ?? 0;
    return targetTierValue > currentTierValue;
  };

  // Check if this is the current plan
  const isCurrentPlan = (planTier) => {
    if (!currentPlanTier) return false;
    return currentPlanTier.toLowerCase() === planTier?.toLowerCase();
  };

  const handleSelectPlan = (planId) => {
    navigate('/subscription/payment', { state: { planId, billingCycle } });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[#0056B3] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading plans...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Plans & Pricing</h1>
          <p className="text-gray-600">Choose the perfect plan for your organization</p>
        </div>

        {/* Main Content */}
        <div className="space-y-8">
        {/* Billing Toggle */}
        <div className="flex justify-center mb-12">
          <div className="bg-white rounded-lg p-2 shadow">
            <div className="flex space-x-2">
              <Button
                variant={billingCycle === 'monthly' ? 'default' : 'outline'}
                onClick={() => setBillingCycle('monthly')}
                className={billingCycle === 'monthly' ? 'bg-[#0056B3]' : ''}
              >
                Monthly
              </Button>
              <Button
                variant={billingCycle === 'yearly' ? 'default' : 'outline'}
                onClick={() => setBillingCycle('yearly')}
                className={billingCycle === 'yearly' ? 'bg-[#0056B3]' : ''}
              >
                Yearly <Badge className="ml-2 bg-green-500">Save 10%</Badge>
              </Button>
            </div>
          </div>
        </div>

          {/* Plans Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {plans.map((plan, index) => {
              const price = billingCycle === 'monthly' ? plan.price_monthly : plan.price_yearly;
              const isPopular = index === 1;
              const cmsFeatures = getPlanFeatures(plan.plan_tier);
              const isCurrent = isCurrentPlan(plan.plan_tier);
              const canUpgrade = canSelectPlan(plan.plan_tier);
              const isDisabled = !canUpgrade || isCurrent;

              return (
                <Card
                  key={plan.plan_id}
                  className={`relative p-8 ${isPopular ? 'border-2 border-[#0056B3] shadow-2xl' : 'shadow-lg'} ${isCurrent ? 'ring-2 ring-green-500' : ''}`}
                >
                  {isPopular && (
                    <Badge className="absolute top-0 right-0 m-4 bg-[#00AEEF]">
                      Most Popular
                    </Badge>
                  )}
                  {isCurrent && (
                    <Badge className="absolute top-0 left-0 m-4 bg-green-500">
                      Current Plan
                    </Badge>
                  )}

                  <div className="mb-6">
                    <h3 className="text-[22px] font-bold mb-2" style={{ color: '#0056B3' }}>
                      {plan.plan_name}
                    </h3>
                    <div className="flex items-baseline mb-4">
                      <span className="text-4xl font-bold">₹{price.toLocaleString('en-IN')}</span>
                      <span className="text-gray-500 ml-2">/{billingCycle === 'monthly' ? 'month' : 'year'}</span>
                    </div>
                  </div>

                  <div className="mb-6">
                    <Button
                      onClick={() => handleSelectPlan(plan.plan_id)}
                      disabled={isDisabled}
                      className={`w-full ${isPopular && canUpgrade ? 'bg-[#0056B3] hover:bg-[#0056B3]/90' : ''} ${isDisabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                      variant={isDisabled ? 'outline' : 'default'}
                    >
                      {isCurrent ? (
                        <>
                          <Check className="w-4 h-4 mr-2" />
                          Current Plan
                        </>
                      ) : canUpgrade ? (
                        'Select Plan'
                      ) : (
                        <>
                          <Lock className="w-4 h-4 mr-2" />
                          Downgrade Not Available
                        </>
                      )}
                    </Button>
                    {!canUpgrade && !isCurrent && (
                      <p className="text-xs text-gray-500 text-center mt-2">
                        You can only upgrade to a higher tier plan
                      </p>
                    )}
                  </div>

                  <div className="space-y-3">
                    <p className="font-semibold text-sm text-gray-700">Features:</p>
                    {cmsFeatures.length > 0 ? (
                      cmsFeatures.map((feature, idx) => (
                        <div key={idx} className="flex items-start space-x-2">
                          {feature.is_included ? (
                            <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          ) : (
                            <X className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                          )}
                          <span className={`text-sm ${feature.is_included ? 'text-gray-700' : 'text-gray-400 line-through'} ${feature.is_bold ? 'font-semibold' : ''}`}>
                            {feature.text}
                          </span>
                        </div>
                      ))
                    ) : (
                      /* Fallback to plan.features if CMS not loaded */
                      Array.isArray(plan.features) && plan.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start space-x-2">
                          <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-gray-700">
                            {typeof feature === 'string' ? feature : feature.feature_name || feature.text}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default PlansPage;
